import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import SearchInput from "@/components/ui/search-input";
import FilterMenuItem from "@/components/controls/ToolbarActions/ToolbarFilterButton/FilterMenuItem";
import { EntityFilters } from "@/components/GlobalSearch/components/EntityFilters";
import {
  GlobalSearchEntityTypes,
  GlobalSearchFilterTypes,
  QUERY_BY_NAME_FIELDS
} from "@/components/GlobalSearch/types";
import { useGlobalSearch } from "@/components/GlobalSearch/hooks/useGlobalSearch";
import { getEntityTypeByEntityId } from "@/entities/tasks/taskLinks";
import {
  renderTaskLinkIcon,
  getTaskLinkTypeKey,
  getFilterEntityTypes
} from "@/components/layouts/TasksDrawer/taskLinkTypes";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { TaskLink } from "@/entities/tasks/types";

const TRANSLATION_KEY = "tasks.createModal.linkToPanel";

interface Props {
  linkedIds: string[];
  suggestions: TaskLink[];
  originName?: string;
  onSelect: (link: TaskLink) => void;
}

const LinkToSearchPanel: React.FC<Props> = ({
  linkedIds,
  suggestions,
  originName,
  onSelect
}) => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<GlobalSearchFilterTypes>(
    GlobalSearchFilterTypes.ALL
  );

  const { enabled: showPartnerships } = useFeatureFlag(
    FeatureFlags.CRM_7805_PARTNERSHIPS
  );
  const enablePartnerships = useEnablePartnerships();
  const partnershipsEnabled = showPartnerships && enablePartnerships;

  const enabledEntityTypes = useMemo(
    () =>
      partnershipsEnabled
        ? undefined
        : Object.values(GlobalSearchEntityTypes).filter(
            (entityType) =>
              entityType !== GlobalSearchEntityTypes.PARTNERS &&
              entityType !== GlobalSearchEntityTypes.PARTNERS_CONTACTS
          ),
    [partnershipsEnabled]
  );

  const hiddenFilters = useMemo(
    () => (partnershipsEnabled ? [] : [GlobalSearchFilterTypes.PARTNERS]),
    [partnershipsEnabled]
  );

  const {
    query,
    results,
    isLoading,
    setQuery,
    setActiveFilter: setSearchFilter
  } = useGlobalSearch(enabledEntityTypes, QUERY_BY_NAME_FIELDS);

  const handleFilterChange = (filter: GlobalSearchFilterTypes) => {
    setActiveFilter(filter);
    setSearchFilter(filter);
  };

  const linkedIdSet = useMemo(() => new Set(linkedIds), [linkedIds]);
  const hasQuery = !isEmpty(query.trim());

  // No name/other split here: the request is scoped to name fields, so every
  // result is a name match. GlobalSearch still partitions — it searches unscoped.
  const resultLinks = useMemo(
    () =>
      results.reduce<TaskLink[]>((links, result) => {
        if (linkedIdSet.has(result.id)) return links;
        links.push({
          entityId: result.id,
          entityName: result.name,
          entityType: getEntityTypeByEntityId(result.id)
        });
        return links;
      }, []),
    [results, linkedIdSet]
  );

  const suggestedLinks = useMemo(() => {
    const allowedTypes = getFilterEntityTypes(activeFilter);
    return suggestions.filter(
      (link) =>
        !linkedIdSet.has(link.entityId) &&
        (!allowedTypes ||
          (link.entityType !== undefined &&
            allowedTypes.includes(link.entityType)))
    );
  }, [suggestions, linkedIdSet, activeFilter]);

  const visibleLinks = hasQuery ? resultLinks : suggestedLinks;

  const renderRow = (link: TaskLink) => (
    <FilterMenuItem
      key={link.entityId}
      layout="compact"
      onClick={() => onSelect(link)}
      dataTest={`link-to-option-${link.entityId}`}
    >
      <div className="flex items-center gap-2 w-full min-w-0">
        {renderTaskLinkIcon(link.entityType, "secondary")}
        <Text variant="body" className="flex-1 truncate">
          {link.entityName}
        </Text>
        <Text variant="smBodyTertiary" className="flex-shrink-0">
          {t(getTaskLinkTypeKey(link.entityType))}
        </Text>
      </div>
    </FilterMenuItem>
  );

  return (
    <div
      className="flex flex-col gap-2 w-[32rem]"
      data-test="link-to-search-panel"
    >
      <SearchInput
        value={query}
        onChange={setQuery}
        placeholder={t(`${TRANSLATION_KEY}.searchPlaceholder`)}
      />
      <EntityFilters
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
        hiddenFilters={hiddenFilters}
      />
      <div className="flex flex-col max-h-[18rem] overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Spinner size="sm" />
          </div>
        ) : isEmpty(visibleLinks) ? (
          <Text variant="bodyTertiary" className="px-2 py-4 text-center">
            {t("tables.emptyState.title")}
          </Text>
        ) : (
          <>
            {hasQuery ? null : (
              <Text variant="smBodyTertiary" className="px-2 py-1">
                {originName
                  ? t(`${TRANSLATION_KEY}.suggestedFrom`, { name: originName })
                  : t(`${TRANSLATION_KEY}.suggested`)}
              </Text>
            )}
            {visibleLinks.map(renderRow)}
          </>
        )}
      </div>
      {hasQuery ? null : (
        <Text
          variant="smBodyTertiary"
          className="px-2 pt-1"
          data-test="link-to-search-hint"
        >
          {t(`${TRANSLATION_KEY}.typeToSearch`)}
        </Text>
      )}
    </div>
  );
};

export default LinkToSearchPanel;
